import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalModule, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { ToolbarModule } from 'primeng/toolbar';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { UserService } from './service/user.service';
import { SettingsService } from './service/settings.service';
import { GA4Service } from './service/ga4.service';
import { ToastModule } from 'primeng/toast';
import { LanguageService } from './service/language.service';
import { Title } from '@angular/platform-browser';
import { environment } from '../environments/environments';
import { GeneralFeedbackDialogComponent } from './components/general-feedback-dialog/general-feedback-dialog.component';

declare let newrelic: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    MsalModule,
    RouterOutlet,
    RouterLink,
    ToolbarModule,
    ButtonModule,
    MenuModule,
    ToastModule,
    GeneralFeedbackDialogComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  isSurveyEnabled = false;
  userId: string | null = null;

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userService: UserService,
    private settingsService: SettingsService,
    private router: Router,
    private gAService: GA4Service,
    private languageService: LanguageService,
    private title: Title
  ) {}

  ngOnInit(): void {
    this.title.setTitle(environment.title);

    this.checkAndSetActiveAccount();
    this.subscribeToRouterEvents();
    this.handleAuthRedirect();
    this.enableAccountStorageEvents();
    this.subscribeToMsalSubject();
    this.subscribeToInProgress();
  }

  private subscribeToRouterEvents(): void {
    this.router.events.pipe(takeUntil(this._destroying$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Reporta ao New Relic a troca de rota
        newrelic.interaction().setName(`Route Change: ${event.urlAfterRedirects}`);
      }
    });
  }

  private handleAuthRedirect(): void {
    this.authService.handleRedirectObservable().subscribe((response: AuthenticationResult | null) => {
      if (response && response.account) {
        this.authService.instance.setActiveAccount(response.account);
      }
    });
  }

  private enableAccountStorageEvents(): void {
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
  }

  private subscribeToMsalSubject(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.authService.instance.setActiveAccount(accounts[0]);
        } else {
          window.location.pathname = '/';
          this.languageService.setDefaultLanguage();
        }
      });
  }

  private subscribeToInProgress(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.userId = this.userService.getUserId(); // NOVO: usado para a chave do localStorage da pesquisa
        this.saveNewUser();
        this.languageService.setDefaultLanguage();
        this.setTagManager();
      });
  }

  private setTagManager(): void {
    this.settingsService.get().subscribe((settings) => {
      const tagId = settings?.tenantSettings?.tagManagerId ?? '';
      this.gAService.initializeGTM(tagId);
      this.isSurveyEnabled = settings?.isFeedbackEnabled ?? false; // NOVO
    });
  }

  private saveNewUser(): void {
    this.userService.getLoggedUserInformation();
  }

  private checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const account = this.authService.instance.getAllAccounts()[0];
      this.authService.instance.setActiveAccount(account);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
